import { useState } from 'react';
import { Frame } from './Frame';
import { KINDS, KIND_STEP, ABOUT_STEP, ABOUT_PLACEHOLDER, aboutBody, kindChip } from '../data/intro-copy';
import { track } from '../analytics';

/**
 * The intro's idea step (the `idea_step` experiment, lib/experiments.ts,
 * well/product/33 §8): two screens between the dream question and the first
 * card, for the imagine starter. Intro.tsx owns where they are and what was
 * said; these draw the screens and count what happened. What anybody types is
 * never sent with an event: only its length and how long it took.
 */

/**
 * What kind of thing they want to make. A tap on a chip moves on at once:
 * this one should take a second. "Something else" swaps the chips for a short
 * box, so the screen never has to hold both.
 */
export function KindStep({ dream, chosen, onPick, onBack }: { dream: string; chosen: string; onPick: (label: string) => void; onBack: () => void }) {
  const kinds = KINDS[dream];
  const [other, setOther] = useState(() => !!chosen && !kinds.some((k) => k.label === chosen));
  const [typed, setTyped] = useState(() => (kinds.some((k) => k.label === chosen) ? '' : chosen));
  const clean = typed.replace(/\s+/g, ' ').trim().slice(0, KIND_STEP.max);
  const pick = (k: { id: string; label: string }) => {
    track('intro_kind', { dream, kind_id: k.id, how: 'chip' });
    onPick(k.label);
  };
  const pickTyped = () => {
    if (!clean) return;
    track('intro_kind', { dream, kind_id: 'other', how: 'typed' });
    onPick(clean);
  };
  return (
    <Frame theme="fit" left={<span className="tag">Day 1</span>}>
      <div className="intro">
        <h1>{KIND_STEP.title}</h1>
        <p className="why">{KIND_STEP.body}</p>
        {other ? (
          <form className="stack" style={{ gap: 6, width: '100%' }} onSubmit={(e) => { e.preventDefault(); pickTyped(); }}>
            <label className="label" htmlFor="idea-kind">{KIND_STEP.otherLabel}</label>
            <input id="idea-kind" className="input hand" type="text" value={typed} maxLength={KIND_STEP.max} placeholder={KIND_STEP.otherPlaceholder}
              autoComplete="off" enterKeyHint="next" onChange={(e) => setTyped(e.target.value)} />
            <button type="button" className="link" style={{ alignSelf: 'flex-start' }} onClick={() => setOther(false)}>{KIND_STEP.otherBack}</button>
          </form>
        ) : (
          <div className="big-opts two">
            {kinds.map((k) => <button key={k.id} className={`big-opt ${chosen === k.label ? 'on' : ''}`} onClick={() => pick(k)}>{kindChip(k.label)}</button>)}
            <button className="big-opt" onClick={() => setOther(true)}>{KIND_STEP.other}</button>
          </div>
        )}
      </div>
      <div className="stack" style={{ marginTop: 'auto' }}>
        {other && <button className="btn coral" disabled={!clean} onClick={pickTyped}>{KIND_STEP.otherDone}</button>}
        <div className="row" style={{ justifyContent: 'center' }}>
          <button className="link" onClick={onBack}>Back</button>
        </div>
      </div>
    </Frame>
  );
}

/**
 * Their last great idea (owner, 2026-10-19; it replaced "what do you want it
 * to be about?", which asked them to invent one on the spot). A box and one
 * quiet example, no sparks: this is where they think. The box sits high, under
 * the title, where the keyboard leaves it. Not having one is an answer, not a
 * skip: a button the same width as the other, and the screen it leads to deals
 * one with them (the Postcard Machine, screens/Imagine.tsx).
 *
 * The events keep their names (`intro_about…`) so the funnels built on them
 * keep reading; `ask: 'last_idea'` tells this question from the old one.
 */
export function AboutStep({ dream, kind, about, setAbout, onDone, onNone, onBack }: {
  dream: string; kind: string; about: string; setAbout: (s: string) => void;
  onDone: (about: string) => void; onNone: () => void; onBack: () => void;
}) {
  // when the screen opened, for think_ms: set once, on mount
  const [shownAt] = useState(() => ({ current: Date.now() }));
  const clean = about.replace(/\s+/g, ' ').trim().slice(0, ABOUT_STEP.max);
  const ready = clean.replace(/\s/g, '').length >= ABOUT_STEP.min;
  const done = () => {
    if (!ready) return;
    track('intro_about', { dream, ask: 'last_idea', given: true, chars: clean.length, think_ms: Date.now() - shownAt.current });
    onDone(clean);
  };
  const none = () => {
    track('intro_about_skipped', { dream, ask: 'last_idea', to: 'machine', typed: clean.length, think_ms: Date.now() - shownAt.current });
    onNone();
  };
  return (
    <Frame theme="fit" left={<span className="tag">Day 1</span>}>
      <div className="intro">
        <h1>{ABOUT_STEP.title}</h1>
        <p className="why">{aboutBody(kind)}</p>
        <textarea
          className="input hand idea-about"
          rows={4}
          value={about}
          maxLength={ABOUT_STEP.max}
          placeholder={ABOUT_PLACEHOLDER[dream]}
          aria-label={ABOUT_STEP.label}
          enterKeyHint="done"
          onChange={(e) => setAbout(e.target.value.replace(/\n/g, ' '))}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); done(); } }}
        />
      </div>
      <div className="stack" style={{ marginTop: 'auto' }}>
        <button className="btn coral" disabled={!ready} onClick={done}>{ABOUT_STEP.done}</button>
        <button className="btn butter" onClick={none}>{ABOUT_STEP.none}</button>
        <p className="hint" style={{ textAlign: 'center', margin: 0 }}>{ABOUT_STEP.noneHint}</p>
        <div className="row" style={{ justifyContent: 'center' }}>
          <button className="link" onClick={onBack}>Back</button>
        </div>
      </div>
    </Frame>
  );
}
